import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';
import { useAuth, apiCall } from '../contexts/AuthContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_publishable_key');

interface Product {
  id: string;
  product_template_id: string;
  name: string;
  description: string;
  subscription_types: {
    monthly: number;
    annual: number;
  };
}

interface SubscriptionFormProps {
  product: Product;
  subscriptionType: 'monthly' | 'annual';
  onSuccess?: () => void;
  onCancel?: () => void;
}

const CheckoutForm: React.FC<SubscriptionFormProps> = ({
  product,
  subscriptionType,
  onSuccess,
  onCancel
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user, token } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<{
    promotionCodeId: string;
    percentOff: number | null;
    amountOff: number | null;
    name: string;
  } | null>(null);

  const amount = product.subscription_types[subscriptionType];
  const period = subscriptionType === 'monthly' ? 'month' : 'year';

  const discountedAmount = appliedCoupon
    ? appliedCoupon.percentOff != null
      ? Math.max(0, amount - Math.round(amount * appliedCoupon.percentOff / 100))
      : appliedCoupon.amountOff != null
        ? Math.max(0, amount - appliedCoupon.amountOff)
        : amount
    : amount;
  const displayAmount = (discountedAmount / 100).toFixed(0);

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setError(null);
    try {
      const response = await apiCall('/subscriptions/validate-coupon', {
        method: 'POST',
        body: JSON.stringify({ couponCode: couponInput.trim() })
      }, token);
      const data = await response.json();
      if (!response.ok || !data.success) {
        setError(data.message || 'Invalid coupon code');
        setAppliedCoupon(null);
        return;
      }
      setAppliedCoupon(data.data);
      toast.success(`Coupon applied: ${data.data.name}`);
    } catch {
      setError('Failed to validate coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || !user) {
      toast.error('Payment system not ready or user not authenticated');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Create subscription payment intent
      const response = await apiCall('/subscriptions/create', {
        method: 'POST',
        body: JSON.stringify({
          productId: product.id,
          subscriptionType: subscriptionType
        })
      }, token);

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to create subscription');
      }

      const { customerId } = data.data;

      if (!customerId) {
        throw new Error('Missing customer information from subscription setup');
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      const paymentMethodResult = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
        },
      });

      if (paymentMethodResult.error || !paymentMethodResult.paymentMethod) {
        throw new Error(paymentMethodResult.error?.message || 'Failed to create payment method');
      }

      const confirmResponse = await apiCall('/subscriptions/confirm', {
        method: 'POST',
        body: JSON.stringify({
          customerId,
          productId: product.id,
          subscriptionType,
          paymentMethodId: paymentMethodResult.paymentMethod.id,
          ...(appliedCoupon ? { promotionCodeId: appliedCoupon.promotionCodeId } : {})
        })
      }, token);

      const confirmData = await confirmResponse.json();

      if (!confirmResponse.ok || !confirmData.success) {
        throw new Error(confirmData.message || 'Failed to confirm subscription');
      }

      const stripeData = confirmData.data?.stripe;

      // Trial subscription: confirm the card setup intent so Stripe can charge the
      // card automatically when the trial ends (no user interaction required then).
      if (stripeData?.setupIntentClientSecret) {
        const { error: setupError } = await stripe.confirmCardSetup(
          stripeData.setupIntentClientSecret,
          { payment_method: paymentMethodResult.paymentMethod.id }
        );
        if (setupError) {
          throw new Error(setupError.message || 'Failed to authorize card for future payments');
        }
      }

      // Non-trial subscription: handle 3DS challenge if the bank requires it.
      if (stripeData?.paymentIntentClientSecret) {
        const { error: payError } = await stripe.confirmCardPayment(
          stripeData.paymentIntentClientSecret,
          { payment_method: paymentMethodResult.paymentMethod.id }
        );
        if (payError) {
          throw new Error(payError.message || 'Payment confirmation failed');
        }
      }

      const card = elements.getElement(CardElement);
      card?.clear();
      toast.success('Subscription created successfully!');
      window.dispatchEvent(new Event('subscription-updated'));
      onSuccess?.();
    } catch (err: any) {
      const errorMessage = err.message || 'An error occurred during payment';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="subscription-form">
      <div className="form-header">
        <h2>Complete Your Subscription</h2>
        <div className="subscription-summary">
          <h3>{product.name}</h3>
          <div className="pricing-summary">
            {appliedCoupon && discountedAmount !== amount && (
              <span className="original-amount" style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                ${(amount / 100).toFixed(0)}
              </span>
            )}
            <span className="amount">${displayAmount}</span>
            <span className="period">/{period}</span>
          </div>
          {appliedCoupon && (
            <p className="coupon-applied" style={{ color: '#22c55e', fontSize: 13, margin: '4px 0' }}>
              Coupon "{appliedCoupon.name}" applied
              {appliedCoupon.percentOff != null ? ` — ${appliedCoupon.percentOff}% off` : ''}
            </p>
          )}
          <p className="billing-info">
            {subscriptionType === 'annual' ? 'Billed annually' : 'Billed monthly'} • Auto-renewal enabled
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="payment-form">
        <div className="form-section">
          <h4>Promo Code</h4>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              placeholder="Enter promo code"
              disabled={!!appliedCoupon || couponLoading}
              style={{
                flex: 1,
                padding: '10px 12px',
                border: '1px solid #e0e0e0',
                borderRadius: 6,
                fontSize: 14,
                outline: 'none'
              }}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleApplyCoupon())}
            />
            {appliedCoupon ? (
              <button
                type="button"
                onClick={() => { setAppliedCoupon(null); setCouponInput(''); }}
                style={{
                  padding: '10px 16px',
                  background: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14
                }}
              >
                Remove
              </button>
            ) : (
              <button
                type="button"
                onClick={handleApplyCoupon}
                disabled={!couponInput.trim() || couponLoading}
                style={{
                  padding: '10px 16px',
                  background: '#1a1a2e',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  opacity: !couponInput.trim() || couponLoading ? 0.6 : 1
                }}
              >
                {couponLoading ? 'Checking...' : 'Apply'}
              </button>
            )}
          </div>
        </div>

        <div className="form-section">
          <h4>Payment Information</h4>
          <div className="card-element-container">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="cancel-button"
            disabled={processing}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!stripe || processing}
            className="submit-button"
          >
            {processing
              ? 'Processing...'
              : discountedAmount === 0
                ? 'Subscribe for Free'
                : `Subscribe for $${displayAmount}/${period}`
            }
          </button>
        </div>

        <div className="subscription-terms">
          <p>
            By subscribing, you agree to our terms of service. Your subscription will automatically 
            renew {subscriptionType === 'monthly' ? 'monthly' : 'annually'} unless cancelled.
          </p>
        </div>
      </form>
    </div>
  );
};

const SubscriptionForm: React.FC<SubscriptionFormProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm {...props} />
    </Elements>
  );
};

export default SubscriptionForm;
